import type { UseLiqPrice } from "@components/hooks/useLiqPrice";

import { BN } from "@utils/BN";

import type { Market, Position } from "@src/api";

/**
 * Calculates the side of a position based on size
 * @param size - Position size as BN
 * @returns "long" if size > 0, "short" if size < 0
 */
export const calculatePositionSide = (size: BN): "long" | "short" => {
  return size.isGreaterThan(0) ? "long" : "short";
};

/**
 * Calculates the price difference between mark price and entry price
 * @param markPrice - Current market price
 * @param entryPrice - Position entry price
 * @returns Price difference as BN
 */
export const calculatePriceDifference = (markPrice: BN, entryPrice: BN): BN => {
  return markPrice.minus(entryPrice);
};

/**
 * Calculates unrealized PnL for a position
 * Formula: Position Size * (Mark Price - Average Entry Price)
 * @param diffPrice - Price difference (mark - entry) in decimal format
 * @param size - Signed position size (positive for long, negative for short) in decimal format
 * @returns PnL in USDC as BN
 */
export const calculateUnrealizedPnL = (diffPrice: BN, size: BN): BN => {
  return size.multipliedBy(diffPrice);
};

/**
 * Calculates position value in USD at current mark price
 * @param size - Position size
 * @param markPrice - Current market price
 * @param baseDecimals - Base token decimals
 * @param quoteDecimals - Quote token decimals
 * @returns Value in USD as BN
 */
export const calculatePositionValueUsd = (size: BN, markPrice: BN, baseDecimals: number, quoteDecimals: number): BN => {
  return BN.formatUnits(size, baseDecimals).multipliedBy(BN.formatUnits(markPrice, quoteDecimals).toString());
};

/**
 * Calculates position value at entry price
 * @param size - Position size
 * @param entryPrice - Entry price
 * @param baseDecimals - Base token decimals
 * @param quoteDecimals - Quote token decimals
 * @returns Entry value in USD as BN
 */
export const calculateEntryValueUsd = (size: BN, entryPrice: BN, baseDecimals: number, quoteDecimals: number): BN => {
  return BN.formatUnits(size, baseDecimals).multipliedBy(BN.formatUnits(entryPrice, quoteDecimals));
};

/**
 * Calculates leverage for a position
 * @param entryValueUsd - Position value at entry price
 * @param margin - Margin amount
 * @returns Leverage as BN (rounded up to integer)
 */
export const calculateLeverage = (entryValueUsd: BN, margin: BN): BN => {
  return new BN(entryValueUsd.dividedBy(margin).integerValue(BN.ROUND_UP).toString());
};

/**
 * Calculates PnL percentage based on position value and margin
 * @param pnlUsd - Unrealized PnL in USD
 * @param margin - Margin amount
 * @returns PnL percentage as BN
 */
export const calculatePnLPercentage = (pnlUsd: BN, margin: BN): BN => {
  return margin.isZero() ? BN.ZERO : pnlUsd.dividedBy(margin).multipliedBy(100);
};

/**
 * Calculates the total Maintenance Margin Requirement (MMR) for all positions
 * @param positions - All positions in the account
 * @param markets - All markets data
 * @returns Total MMR as number
 */
export const calculateTotalAccountMMR = (positions: Position[], markets: Market[]): number => {
  let totalMMR = 0;

  for (const position of positions) {
    const market = markets.find((m) => m.addr === position.market_addr);
    if (!market) continue;

    const { mark_price, base_decimals, quote_decimals, maintenance_margin_ratio } = market;
    const markPrice = new BN(mark_price);
    const sizeBN = new BN(position.size);

    // Position Notional Value = |Position Size| * Mark Price
    const positionValue = calculatePositionValueUsd(sizeBN.abs(), markPrice, base_decimals, quote_decimals);

    // Position MMR = Position Notional Value * Maintenance Margin Ratio
    const mmrRatio = new BN(maintenance_margin_ratio);
    const positionMMR = positionValue.multipliedBy(mmrRatio);

    totalMMR += Number.parseFloat(positionMMR.toString());
  }

  return totalMMR;
};

/**
 * Calculates the liquidation price for a **linear** product (approx, continuous math).
 *
 * This matches the backend solver `ekiden_math::risk::liquidation_price_linear`:
 *
 * Solve for `M` at the boundary:
 * - Equity(M) = Balance + S * (M - E) + F
 * - Maint(M)  = |S| * M * mmr
 * - Boundary: Equity(M) = Maint(M)
 *
 * Result:
 *   M_liq = (Balance + F - E*S) / (|S|*mmr - S)
 *
 * Notes:
 * - `S` must be **signed** (long > 0, short < 0).
 * - `Balance` and `F` must be in **quote** units (e.g. USDC).
 * - `E`/`M` must be in **quote per base** units (e.g. USDC per BTC).
 * - Returns `null` for invalid inputs (zero size, negative ratios, zero denominator, negative result).
 */
export const calculateLiquidationPriceLinear = (params: {
  balance: BN;
  entryPrice: BN;
  signedSize: BN;
  mmr: BN;
  funding?: BN; // net funding PnL: receive +, pay -
}): BN | null => {
  const { balance, entryPrice, signedSize, mmr, funding = BN.ZERO } = params;

  if (signedSize.isZero()) return null;
  if (mmr.isLessThan(0)) return null;

  const absS = signedSize.abs();
  const denom = absS.multipliedBy(mmr).minus(signedSize);
  if (denom.isZero()) return null;

  const px = balance.plus(funding).minus(entryPrice.multipliedBy(signedSize)).dividedBy(denom);
  if (px.isLessThan(0)) return null;
  return px;
};

/**
 * Calculates the bankruptcy (a.k.a. "bust") price for a **linear** product (approx, continuous math).
 *
 * Used when you want the price where equity covers **exactly** the closing/liquidation fee:
 *
 *   Balance + S*(M - E) + F = |S| * M * feeRate
 *
 * Result:
 *   M_bust = (Balance + F - E*S) / (|S|*feeRate - S)
 *
 * If you want the "zero equity" price instead, set `feeRate = 0`.
 */
export const calculateBankruptcyPriceLinear = (params: {
  balance: BN;
  entryPrice: BN;
  signedSize: BN;
  feeRate: BN; // e.g. liquidation_fee_rate as a decimal (0.002 = 0.2%)
  funding?: BN; // net funding PnL: receive +, pay -
}): BN | null => {
  const { balance, entryPrice, signedSize, feeRate, funding = BN.ZERO } = params;

  if (signedSize.isZero()) return null;
  if (feeRate.isLessThan(0)) return null;

  const absS = signedSize.abs();
  const denom = absS.multipliedBy(feeRate).minus(signedSize);
  if (denom.isZero()) return null;

  const px = balance.plus(funding).minus(entryPrice.multipliedBy(signedSize)).dividedBy(denom);
  if (px.isLessThan(0)) return null;
  return px;
};

/**
 * Calculates cross-margin liquidation price for a position
 * Formula: LP = Max(0, Mark Price * [1 - Maintenance Margin Excess / (Position Size * Mark Price)])
 * Where: Maintenance Margin Excess = Total Account Equity - Total Account MMR
 * @param markPrice - Current mark price
 * @param size - Signed position size (negative for short, positive for long)
 * @param accountEquity - Total account equity
 * @param totalAccountMMR - Total maintenance margin requirement for all positions
 * @returns Liquidation price as BN
 */
export const calculateCrossMarginLiquidationPrice = (
  markPrice: BN,
  size: BN,
  accountEquity: number,
  totalAccountMMR: number,
): BN => {
  // Maintenance Margin Excess = Total Account Equity - MMR
  const maintenanceMarginExcess = accountEquity - totalAccountMMR;

  // Position Size * Mark Price (using absolute value to avoid division issues)
  const positionSizeTimesMarkPrice = size.multipliedBy(markPrice);

  // If position size is zero, return -1 to indicate invalid
  if (positionSizeTimesMarkPrice.isZero()) {
    return new BN(-1);
  }

  // Calculate: 1 - (Maintenance Margin Excess / (Position Size * Mark Price))
  const ratio = new BN(1).minus(new BN(maintenanceMarginExcess).dividedBy(positionSizeTimesMarkPrice));

  // Liquidation Price = Mark Price * ratio
  const liqPrice = markPrice.multipliedBy(ratio);

  // Return Max(0, liqPrice)
  return BN.max(BN.ZERO, liqPrice);
};

/**
 * Interface for imaginary position data after order fills
 */
export interface ImaginaryPositionData {
  /** Signed position size (negative for short, positive for long) */
  size: BN;
  /** Position value in USD */
  positionValue: BN;
  /** Required margin for this position */
  marginRequired: BN;
}

/**
 * Calculates an imaginary position that would result from an order being filled
 * Takes into account any existing position in the same market
 * @param orderSize - Order size (absolute value)
 * @param orderSide - Order side ("buy" for long, "sell" for short)
 * @param orderPrice - Order limit price (or mark price for market orders)
 * @param leverage - Leverage for the order
 * @param marketAddr - Market address
 * @param existingPositions - All existing positions in the account
 * @param baseDecimals - Base token decimals
 * @param quoteDecimals - Quote token decimals
 * @returns Imaginary position data
 */
export const calculateImaginaryPosition = (
  orderSize: BN,
  orderSide: "buy" | "sell",
  orderPrice: BN,
  leverage: number,
  marketAddr: string,
  existingPositions: Position[],
  _baseDecimals: number,
  _quoteDecimals: number,
): ImaginaryPositionData => {
  // Find existing position in the same market
  const existingPosition = existingPositions.find((p) => p.market_addr === marketAddr);

  // Calculate order size as signed value (positive for buy, negative for sell)
  const signedOrderSize = orderSide === "buy" ? orderSize : orderSize.negated();

  // Calculate new position size
  const existingSize = existingPosition ? new BN(existingPosition.size) : BN.ZERO;
  const newSize = existingSize.plus(signedOrderSize);

  // Calculate position value at order price
  // Note: orderSize and orderPrice are already in human-readable format, so just multiply directly
  const positionValue = newSize.abs().multipliedBy(orderPrice);

  // Calculate margin required for the order
  // Margin = Position Value / Leverage
  const marginRequired = positionValue.dividedBy(leverage);

  return {
    size: newSize,
    positionValue,
    marginRequired,
  };
};

/**
 * Calculates cross-margin liquidation price for an order being created
 * Creates an imaginary position based on the order and calculates liquidation price
 * @param orderSize - Order size (absolute value)
 * @param orderSide - Order side ("buy" for long, "sell" for short)
 * @param orderPrice - Order limit price (or mark price for market orders)
 * @param leverage - Leverage for the order
 * @param marketAddr - Market address
 * @param markPrice - Current mark price
 * @param existingPositions - All existing positions in the account
 * @param allMarkets - All markets data
 * @param accountEquity - Total account equity
 * @param baseDecimals - Base token decimals
 * @param quoteDecimals - Quote token decimals
 * @param maintenanceMarginRatio - Maintenance margin ratio for the market
 * @param takerFeeRate - Taker fee rate for the market
 * @returns Liquidation price as BN
 */
export const calculateOrderLiquidationPrice = (
  orderSize: BN,
  orderSide: "buy" | "sell",
  orderPrice: BN,
  leverage: number,
  marketAddr: string,
  markPrice: BN,
  existingPositions: Position[],
  allMarkets: Market[],
  accountEquity: number,
  baseDecimals: number,
  quoteDecimals: number,
  maintenanceMarginRatio: number,
  takerFeeRate: number,
): BN => {
  // If order size is zero, return -1 to indicate invalid
  if (orderSize.isZero()) {
    return new BN(-1);
  }

  // Calculate imaginary position after order fills
  const imaginaryPosition = calculateImaginaryPosition(
    orderSize,
    orderSide,
    orderPrice,
    leverage,
    marketAddr,
    existingPositions,
    baseDecimals,
    quoteDecimals,
  );

  // If resulting position size is zero (order perfectly closes existing position), return -1
  if (imaginaryPosition.size.isZero()) {
    return new BN(-1);
  }

  // Cross-margin: use account equity and total MMR
  // Estimate equity after order fee: equity - orderValue * feeRate
  const orderValue = orderSize.multipliedBy(orderPrice);
  const fee = orderValue.multipliedBy(takerFeeRate);
  const equityAfterFee = accountEquity - fee.toNumber();

  // Calculate total MMR for existing positions + imaginary position for this market
  const otherPositions = existingPositions.filter((p) => p.market_addr !== marketAddr);
  const otherMMR = calculateTotalAccountMMR(otherPositions, allMarkets);

  // MMR for the new imaginary position at current mark price
  const imaginaryMMR = imaginaryPosition.size.abs().multipliedBy(markPrice).multipliedBy(maintenanceMarginRatio);
  const totalMMR = otherMMR + imaginaryMMR.toNumber();

  // LP = Max(0, MP * [1 - (Equity - TotalMMR) / (S * MP)])
  const liqPrice = calculateCrossMarginLiquidationPrice(markPrice, imaginaryPosition.size, equityAfterFee, totalMMR);

  if (liqPrice.isLessThan(0)) {
    return new BN(-1);
  }

  return liqPrice;
};

/**
 * Calculates liquidation price for a position (isolated margin - legacy)
 * @param calc - Liquidation price calculation function from useLiqPrice hook
 * @param entryPrice - Position entry price (formatted)
 * @param leverage - Position leverage
 * @param mmr - Maintenance margin ratio as BN
 * @param size - Position size (absolute value)
 * @param side - Position side ("long" or "short")
 * @returns Liquidation price as BN
 */
export const calculateLiquidationPrice = (
  calc: UseLiqPrice["calc"],
  entryPrice: BN,
  leverage: number,
  mmr: BN,
  size: BN,
  side: "long" | "short",
): BN => {
  return calc({
    entryPrice,
    leverage,
    mmr,
    size: size.abs(),
    side,
  });
};

/**
 * Calculates funding rate for a position
 * @param valueUsd - Position value in USD
 * @param marketRate - Current market funding rate
 * @param fundingIndex - Position funding index
 * @param baseDecimals - Base token decimals
 * @returns Funding amount as BN
 */
export const calculateFundingRate = (
  valueUsd: BN,
  marketRate: number,
  fundingIndex: number,
  baseDecimals: number,
): BN => {
  const diffRate = BN.formatUnits(marketRate - fundingIndex, baseDecimals);
  return valueUsd.multipliedBy(diffRate);
};

/**
 * Formats PnL display string with proper sign and formatting
 * @param pnlUsd - PnL amount in USD
 * @returns Formatted PnL string (e.g., "+$123.45", "-$67.89")
 */
export const formatPnLDisplay = (pnlUsd: BN): string => {
  const isPnlPositive = pnlUsd.isGreaterThan(0);
  const isPnlNegative = pnlUsd.isLessThan(0);
  return `${isPnlPositive ? "+" : isPnlNegative ? "-" : ""}$${pnlUsd.abs().toFixed(2)}`;
};

/**
 * Formats PnL percentage display string
 * @param pnlPercent - PnL percentage
 * @returns Formatted percentage string (e.g., "+12.34", "-5.67")
 */
export const formatPnLPercentageDisplay = (pnlPercent: BN): string => {
  const isPnlPositive = pnlPercent.isGreaterThan(0);
  const isPnlNegative = pnlPercent.isLessThan(0);
  return `${isPnlPositive ? "+" : isPnlNegative ? "" : ""}${pnlPercent.toFixed(2)}`;
};

/**
 * Main function to transform position data with all calculations
 * @param position - Position data from API
 * @param market - Market data
 * @param marketRate - Current market funding rate
 * @param _calc - Liquidation price calculation function (unused, kept for backwards compatibility)
 * @param _mmr - Maintenance margin ratio as BN (unused, kept for backwards compatibility)
 * @param crossMarginData - Optional cross-margin data for liquidation price calculation
 * @returns Transformed position row data or undefined if market not found
 */
export const transformPositionData = (
  position: Position,
  market: Market | undefined,
  _marketRate: number | undefined,
  _calc: UseLiqPrice["calc"],
  _mmr: BN,
  crossMarginData?: {
    allPositions: Position[];
    allMarkets: Market[];
    accountEquity: number;
  },
) => {
  if (!market) return undefined;

  const { mark_price, base_symbol, quote_decimals } = market;
  const { size, price, margin, entry_price, leverage: apiLeverage, tp_price, sl_price } = position;
  // Parse all values to BN
  const markPrice = new BN(mark_price);
  const entryPrice = new BN(entry_price || price);
  const rawSizeBN = new BN(size);
  // Make size signed: positive for buy/long, negative for sell/short
  const sizeBN = position.side === "sell" ? rawSizeBN.negated() : rawSizeBN;
  // marginBN is in RAW units (needs formatUnits for display)
  const marginBN = new BN(margin);
  // Convert margin to human-readable for calculations (same format as size and price)
  const marginHumanReadable = BN.formatUnits(marginBN, quote_decimals);

  // Calculate position side - use API side if available, otherwise calculate from size
  const side = position.side ? (position.side === "buy" ? "long" : "short") : calculatePositionSide(sizeBN);

  // Always calculate unrealized_pnl based on mark price from market (ticker)
  // Formula: Position Size * (Mark Price - Average Entry Price)
  const pnlUsd = calculateUnrealizedPnL(calculatePriceDifference(markPrice, entryPrice), sizeBN);

  // Calculate position values - API values are already in decimal format
  const valueUsd = sizeBN.abs().multipliedBy(markPrice);
  const currentValueUsd = sizeBN.abs().multipliedBy(entryPrice);

  // Use leverage from API if available
  const leverage = apiLeverage !== undefined ? new BN(apiLeverage) : new BN(1);

  // Calculate effective margin for calculations (human-readable format)
  // For cross margin (margin=0), derive from position value / leverage
  let effectiveMargin: BN;
  if (marginHumanReadable.isZero() && !leverage.isZero()) {
    effectiveMargin = currentValueUsd.dividedBy(leverage);
  } else {
    effectiveMargin = marginHumanReadable;
  }

  // Calculate PnL percentage
  const pnlPercent = calculatePnLPercentage(pnlUsd, effectiveMargin);

  // Calculate liquidation price
  let liqPrice: BN;
  if (crossMarginData) {
    // Cross-margin: LP = Max(0, MP * [1 - (Equity - TotalMMR) / (S * MP)])
    const totalMMR = calculateTotalAccountMMR(crossMarginData.allPositions, crossMarginData.allMarkets);
    liqPrice = calculateCrossMarginLiquidationPrice(markPrice, sizeBN, crossMarginData.accountEquity, totalMMR);
  } else {
    // Isolated margin fallback: M_liq = (Balance - E*S) / (|S|*mmr - S)
    const mmr = new BN(market.maintenance_margin_ratio);
    const calculatedLiqPrice = calculateLiquidationPriceLinear({
      balance: effectiveMargin,
      entryPrice,
      signedSize: sizeBN,
      mmr,
    });
    liqPrice = calculatedLiqPrice ?? new BN(-1);
  }

  // Get cumulative funding from position
  const fundingRate = new BN(position.unrealized_funding ?? "0");

  // Format display strings
  const pnlUsdStr = formatPnLDisplay(pnlUsd);
  const pnlPercentStr = formatPnLPercentageDisplay(pnlPercent);

  return {
    market: base_symbol,
    valueUsd: valueUsd.toString(),
    entryPrice,
    markPrice,
    sizeBN,
    marginBN,
    side,
    leverage: leverage.abs().toString(),
    pnlUsd: pnlUsdStr,
    pnlPercent: pnlPercentStr,
    pnlUsdNative: pnlUsd,
    liqPrice,
    fundingRate,
    fundingUnformatted: fundingRate,
    tpPrice: tp_price,
    slPrice: sl_price,
  };
};
